/*
 *  Copyright (c) Qwoach LLC 2020. All rights reserved.
 *  Unauthorized copying of this file, via any medium is strictly prohibited
 *  Proprietary and confidential
 *  Created by Artem Bogush <artem@qwoach.com> on 7/10/2020
 */

import {filterableKey} from "@shared/Filterable/PropertyDecorators";
import DecoratedItem from "@shared/Filterable/DecoratedItem";
import {arrayToTruthyObject, timeNow} from "@/utils/helpers.js";

export default class FilterableItem extends DecoratedItem{

	static filterableKeysMap = []
	static minKeywordLength = 2
	static maxKeywordLength = 5

	sortIndex = null

	constructor(options = {}) {
		super(options);
		this.resetSortIndex(options.sortIndexShift)
	}

	resetSortIndex(sortIndexShift = 0) {
		let sortIndex = (this.createdAt || timeNow()) * 1000 + sortIndexShift
		this.setSortIndex(sortIndex)
	}

	setSortIndex(sortIndex){
		this.sortIndex = sortIndex;
	}

	setSortIndexShift(shift = 0){
		this.sortIndex = Math.trunc(this.sortIndex / 1000) * 1000 + shift
	}

	static normalizeValue(value){
		if(typeof value === "string"){
			value = value.toLowerCase().substring(0, FilterableItem.maxKeywordLength)
		}
		return value
	}


	setupFilterableKeysIfNeeded() {
		let thisClassName = this.getClassName()//this.constructor.name
		// console.log("setupFilterableKeysIfNeeded: this.constructor.name = ", this.constructor.name, ", this.constructor = ", this.constructor, ", this = ", this)
		let keys = FilterableItem.filterableKeysMap[thisClassName]

		// console.log("setupFilterableKeysIfNeeded: keys = ", keys)
		if (!keys){
			keys = []
			keys = this.getAllFilterableKeys()
			let thisClassName = this.getClassName()
			FilterableItem.filterableKeysMap[thisClassName] = keys
		}
		// console.log("buildSearchKeywords: keys = ", keys, ", thisClassName = ", thisClassName, ", this = ", this)
		// console.log("setupFilterableKeysIfNeeded: FilterableItem.filterableKeysMap = ", FilterableItem.filterableKeysMap)

		return keys
	}

	buildSearchKeywords(){
		let keys = this.getComputedFilterableKeys()
		// console.log("buildSearchKeywords: keys = ", keys)
		for(let key of keys){
			let searchKeywords = []
			let value = this[key]
			let options = this.getFilterableOptions(key)

			if(Array.isArray(value)){
				value.forEach(valueItem => this.buildSearchKeywordsInternal(searchKeywords, valueItem, options))
			} else {
				this.buildSearchKeywordsInternal(searchKeywords, value, options)
			}

			let indexPropName = options.indexPropName || "searchKeywords"

			// due to Firestore limitations, we move from using arrays to objects for search indexes
			// more specifically, 'array-contains' can only be used once per query (per disjunction to be more precise)
			// if we use array for search here, we can't use 'array-contains'
			// with other fields when performing search, like filter by sharedWithIds + search
			// https://stackoverflow.com/a/52969138/1713920

			// WARNING: don't use orderBy with search - otherwise this will lead to another limitation that will require
			// compound queries for each property of the search object.
			// Yeah, we have to balance between limitations, which in a lot of cases are there for performance.

			let keywords = this[indexPropName] || {}
			keywords = arrayToTruthyObject(keywords)
			searchKeywords = arrayToTruthyObject(searchKeywords)
			keywords = {...keywords, ...searchKeywords}
			this[indexPropName] = keywords
			// console.log("buildSearchKeywords: indexPropName = ", indexPropName, ", key = ", key, ", options = ", options, ", keywords = ", keywords)

		}
	}

	buildSearchKeywordsInternal(searchKeywords, value, options){

		if(value){
			value = options.transform(value)
		}
		// console.log("buildSearchKeywords: value = ", value)
		if(value && typeof value === "string"){
			let words = value.split(" ")
			for(let word of words){
				if(options.includeFullWord){
					searchKeywords.push(FilterableItem.normalizeValue(this.caseFoldNormalize(word)))
				} else {
					let curWord = ""
					let chars = word.split("") || []
					chars.forEach(letter => {
						curWord += letter
						if(curWord.length >= FilterableItem.minKeywordLength && curWord.length <= FilterableItem.maxKeywordLength){
							searchKeywords.push(this.caseFoldNormalize(curWord))
						}
					})
				}
			}
		}
	}

	beforeSaveToDb(options){
		super.beforeSaveToDb(options)
		this.buildSearchKeywords()
		// console.log("buildSearchKeywords beforeSaveToDb: searchKeywords = ", this.searchKeywords)
	}

	filter(keyword){
		if(!keyword || !keyword.trim().length){
			return true
		}
		keyword = keyword.trim().toLowerCase()
		let keys = this.getComputedFilterableKeys()
		for(let key of keys){
			let value = this[key]
			if(value && this.isFilterMatch(value, keyword, key)){
				return true
			}
		}

		return false
	}

	isFilterMatch(value, keyword, key){
		return (value + "").toLowerCase().indexOf(keyword) >= 0
	}

	getComputedFilterableKeys(){
		return this.setupFilterableKeysIfNeeded()
	}


	getAllFilterableKeys(){
		let filterableKeys = []
		Object.keys(this).forEach(key => {
			if(this.isFilterable(key)){
				filterableKeys.push(key)
			}
		})
		return filterableKeys
	}

	isFilterable(propertyKey){
		let options = Reflect.getMetadata(filterableKey, this, propertyKey);
		// console.log("buildSearchKeywords isFilterable: ", options, ", propertyKey = ", propertyKey)
		return !!options && options.isFilterable
	}
	getFilterableTransform(propertyKey){
		let {transform} = this.getFilterableOptions(propertyKey)
		return transform
	}
	getFilterableOptions(propertyKey){
		let options = Reflect.getMetadata(filterableKey, this, propertyKey);
		if(!options){
			console.error("getFilterableOptions: options are null for propertyKey = ", propertyKey)
			options = {}
		}
		let transformDefault = (item) => item
		options.transform = options.transform || transformDefault
		return options
	}



}
